<template>
  <span
    class="prepeers-tag"
    :class="rootClasses">
    <PrepeersSelectionCheck
      v-if="props.selected && !props.deselectable"
      class="prepeers-tag__check" />
    <span class="prepeers-tag__label">
      {{ props.label }}
      <span
        v-if="props.description"
        class="prepeers-tag__description">
        · {{ props.description }}
      </span>
    </span>
    <button
      v-if="showRemoveButton"
      class="prepeers-tag__remove"
      type="button"
      :aria-label="props.removeAriaLabel"
      @click.stop="emit('remove')">
      <svg
        class="prepeers-tag__remove-icon"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true">
        <path
          d="M2 2l8 8M10 2l-8 8"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round" />
      </svg>
    </button>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComputedRef, PropType } from 'vue'

import PrepeersSelectionCheck from '~/components/ui/PrepeersSelectionCheck.vue'
import type { PrePeersTagAppearance, PrePeersTagProps, PrePeersTagSize } from '~/types/PrePeersTag'

/**
 * Pastille avec check vert (tunnel SEA, filtres) ; croix optionnelle si `removable` ou `deselectable`.
 */
const props: PrePeersTagProps = defineProps({
  label: {
    type: String,
    required: true,
  },
  description: {
    type: String as PropType<string | null>,
    default: null,
  },
  selected: {
    type: Boolean,
    default: true,
  },
  removable: {
    type: Boolean,
    default: false,
  },
  deselectable: {
    type: Boolean,
    default: false,
  },
  removeAriaLabel: {
    type: String,
    default: 'Retirer',
  },
  appearance: {
    type: String as PropType<PrePeersTagAppearance>,
    default: 'pill',
  },
  size: {
    type: String as PropType<PrePeersTagSize>,
    default: 'md',
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  remove: []
}>()

/**
 * Affiche le bouton × (retrait explicite ou désélection).
 * @returns `true` si `removable`, ou si `deselectable` et sélectionné.
 */
const showRemoveButton: ComputedRef<boolean> = computed(
  (): boolean => props.removable || (props.deselectable && props.selected),
)

const rootClasses: ComputedRef<Record<string, boolean>> = computed(
  (): Record<string, boolean> => ({
    'prepeers-tag--pill': props.appearance === 'pill',
    'prepeers-tag--chip': props.appearance === 'chip',
    'prepeers-tag--selected': props.selected,
    'prepeers-tag--inactive': !props.selected,
    'prepeers-tag--size-md': props.size === 'md',
    'prepeers-tag--size-sm': props.size === 'sm',
    'prepeers-tag--compact': props.compact,
  }),
)
</script>

<style scoped>
.prepeers-tag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-body);
  line-height: 1.2;
  /* Taille icône fermer : `compact` en dernier pour l'emporter sur `size-md`. */
  --prepeers-tag-close: 11px;
}

.prepeers-tag--size-sm {
  --prepeers-tag-close: 11px;
}

.prepeers-tag--size-md {
  --prepeers-tag-close: 12px;
}

.prepeers-tag--chip {
  --prepeers-tag-close: 10px;
}

.prepeers-tag--chip.prepeers-tag--size-md {
  --prepeers-tag-close: 11px;
}

.prepeers-tag--chip.prepeers-tag--size-sm {
  --prepeers-tag-close: 10px;
}

.prepeers-tag--compact {
  --prepeers-tag-close: 9px;
}

.prepeers-tag__check {
  flex-shrink: 0;
}

.prepeers-tag__label {
  min-width: 0;
}

.prepeers-tag__description {
  font-weight: 500;
  color: var(--text-muted);
}

/* ── Pill (StepCities, StepSchoolLevel, filtres) ───────────────── */
.prepeers-tag--pill.prepeers-tag--size-md.prepeers-tag--inactive {
  padding: 10px var(--space-4);
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.04);
  border: 1.5px solid var(--border-default);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-weight: 600;
  transition:
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}

.prepeers-tag--pill.prepeers-tag--size-sm.prepeers-tag--inactive {
  padding: 6px 10px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.04);
  border: 1.5px solid var(--border-default);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-weight: 600;
  transition:
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}

.prepeers-tag--pill.prepeers-tag--size-md.prepeers-tag--selected {
  padding: 10px var(--space-4);
  border-radius: var(--radius-full);
  background: rgba(16, 185, 129, 0.15);
  border: 1.5px solid rgba(16, 185, 129, 0.35);
  color: var(--accent-green-lt);
  font-size: var(--text-sm);
  font-weight: 600;
  transition:
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}

.prepeers-tag--pill.prepeers-tag--size-sm.prepeers-tag--selected {
  padding: 6px 10px;
  border-radius: var(--radius-full);
  background: rgba(16, 185, 129, 0.15);
  border: 1.5px solid rgba(16, 185, 129, 0.35);
  color: var(--accent-green-lt);
  font-size: var(--text-sm);
  font-weight: 600;
  transition:
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}

.prepeers-tag--pill.prepeers-tag--selected.prepeers-tag--compact {
  padding: 6px var(--space-3);
  font-size: var(--text-xs);
  font-weight: 600;
}

.prepeers-tag--pill.prepeers-tag--inactive.prepeers-tag--compact {
  padding: 6px var(--space-3);
  font-size: var(--text-xs);
}

/* ── Chip (InterestPicker accordéon) ─────────────────────────── */
.prepeers-tag--chip.prepeers-tag--inactive {
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-default);
  background: var(--bg-surface);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: 500;
  white-space: nowrap;
  transition:
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}

.prepeers-tag--chip.prepeers-tag--selected {
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid rgba(16, 185, 129, 0.5);
  background: rgba(16, 185, 129, 0.1);
  color: var(--accent-green-lt);
  font-size: var(--text-sm);
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.25);
  transition:
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}

.prepeers-tag__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 0;
  border-radius: var(--radius-sm);
}

.prepeers-tag__remove:focus-visible {
  outline: 2px solid var(--accent-blue);
  outline-offset: 1px;
}

.prepeers-tag__remove-icon {
  display: block;
  width: var(--prepeers-tag-close);
  height: var(--prepeers-tag-close);
}
</style>
